"use client";

// Reps : la carte ligue du rail droit (façon Duolingo). L'XP des leçons
// alimente déjà les leagues : la carte relie chaque leçon terminée au
// classement de la semaine. Fetch local silencieux plutôt que useLeagues :
// son catch lève un toast d'erreur, disproportionné pour une carte
// périphérique. Ici une erreur fait simplement disparaître la carte, le rail
// garde ses autres blocs.
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Même habillage que les autres cartes du rail (RepsKeyCard).
const CARD =
    "rounded-xl border border-gray-100 bg-white p-4 dark:border-white/10 dark:bg-white/[0.03]";

export default function RepsLeagueCard() {
    const t = useTranslations();
    const tLeagues = useTranslations("leagues");
    const { data: session } = useSession();
    const [state, setState] = useState({ status: "loading" });

    useEffect(() => {
        if (!session?.user?.userId) return;
        let cancelled = false;
        fetch("/api/leagues/current", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: session.user.userId }),
        })
            .then((r) => r.json())
            .then((result) => {
                if (cancelled) return;
                if (result.success && result.data?.currentLeague) {
                    setState({ status: "joined", data: result.data });
                } else if (result.reason === "inactive") {
                    // Sorti du groupe après une semaine sans XP : même
                    // invitation que jamais-inscrit, avec le blason de son
                    // ancienne ligue.
                    setState({
                        status: "out",
                        level: result.lastLeague?.level || 1,
                    });
                } else if (result.notJoined) {
                    setState({ status: "out", level: 1 });
                } else {
                    setState({ status: "error" });
                }
            })
            .catch(() => {
                if (!cancelled) setState({ status: "error" });
            });
        return () => {
            cancelled = true;
        };
    }, [session?.user?.userId]);

    if (!session?.user || state.status === "error") return null;

    if (state.status === "loading") {
        return (
            <div className={CARD}>
                <div className="flex animate-pulse items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-white/10" />
                    <div className="flex-1 space-y-2">
                        <div className="h-3.5 w-24 rounded bg-gray-100 dark:bg-white/10" />
                        <div className="h-3 w-36 rounded bg-gray-100 dark:bg-white/10" />
                    </div>
                </div>
            </div>
        );
    }

    const joined = state.status === "joined";
    // Blasons /static/icons/leagues/1.svg à 12.svg ; les noms vivent dans le
    // namespace "leagues" (clés "1" à "12"), qu'on LIT sans jamais y toucher.
    const level = Math.min(
        Math.max((joined ? state.data.currentLeague.level : state.level) || 1, 1),
        12,
    );

    const title = joined
        ? t("reps_rail_league_name", { name: tLeagues(String(level)) })
        : tLeagues("not_joined_title");

    const caption = joined
        ? state.data.currentPosition
            ? t("reps_rail_league_status", {
                  position: state.data.currentPosition,
                  xp: state.data.weeklyXp || 0,
              })
            : t("reps_rail_league_week_xp", { xp: state.data.weeklyXp || 0 })
        : t("reps_rail_league_join_hint");

    return (
        <Link
            href="/leagues"
            className={`${CARD} group flex items-center gap-3 transition-colors hover:border-orange-200 dark:hover:border-orange-500/30`}
        >
            <Image
                src={`/static/icons/leagues/${level}.svg`}
                alt=""
                width={48}
                height={48}
                className={`h-12 w-12 shrink-0 drop-shadow-sm ${
                    joined ? "" : "opacity-60 grayscale"
                }`}
            />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900 dark:text-white">
                    {title}
                </span>
                <span className="mt-0.5 block text-xs text-gray-400">
                    {caption}
                </span>
            </span>
            <SolarIcon
                name="alt-arrow-right-linear"
                className="text-lg text-gray-300 transition-colors group-hover:text-orange-500 dark:text-grayDark-600"
            />
        </Link>
    );
}
